import { useState } from 'react';
import { Modal, Box, Button, Typography, CircularProgress } from '@mui/material';

import { DataTable, type TableColumn, PreviewIcon, CheckIcon, XIcon, Label } from '@/src/ui';
import type {
    StudentHistoryDTO,
    StudentExamAttemptDTO,
    StudentExamAttemptQuestionDTO,
} from '../models/studentHistoryDTOts';
import {
    Constants,
    ReportHeaderStyle,
    ReportCellStyle,
    utcToLocal,
    formatOptions,
} from '@/src/utils';
import { apiRequest } from '@/src/services';
import PreviewQuestionModal from './PreviewQuestionModal';

interface StudentAttemptsModalProps {
    student: StudentHistoryDTO;
    attempt: StudentExamAttemptDTO;
    open: boolean;
    onClose: () => void;
}

export default function StudentAttemptsModal({
    student,
    attempt,
    open,
    onClose,
}: StudentAttemptsModalProps) {
    const [previewQuestionData, setPreviewQuestionData] = useState<{
        questionId: number;
        submittedAnswer: string;
        questionAnswer: string | null;
    } | null>(null);

    const [loadingQuestionId, setLoadingQuestionId] = useState<number | null>(null);

    // Sort questions by displayOrder ascending
    const questions = [...(attempt.studentExamAttemptQuestions ?? [])].sort(
        (a, b) => a.displayOrder - b.displayOrder
    );

    // Define table columns
    const columns: TableColumn[] = [
        {
            dataKey: 'displayOrder',
            header: '#',
            align: 'center',
            allowSort: true,
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'questionType',
            header: 'Question Type',
            align: 'left',
            allowSort: true,
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'standards',
            header: 'Standards',
            align: 'left',
            formatter: (row: StudentExamAttemptQuestionDTO) =>
                Array.isArray(row.standards) ? row.standards.join(', ') : '—',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'isCorrect',
            header: 'Correct',
            align: 'center',
            formatter: (row: StudentExamAttemptQuestionDTO) => {
                if (row.questionType === 'Stimulus') {
                    return null;
                }
                return (
                    <Box
                        className="icon-button"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                    >
                        {row.isCorrect ? (
                            <CheckIcon color="green" style={{ width: '28px', height: '28px' }} />
                        ) : (
                            <XIcon color="red" style={{ width: '28px', height: '28px' }} />
                        )}
                    </Box>
                );
            },
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'scoredBy',
            header: 'Scored By',
            align: 'left',
            formatter: (row: StudentExamAttemptQuestionDTO) => {
                if (row.questionType === 'Stimulus') {
                    return null;
                }
                if (!row.isHandScore) return 'N/A';
                else if (!row.scoredBy) return <div className="text-red-500">'Incomplete'</div>;
                else return row.scoredBy;
            },
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'scoredDTUTC',
            header: 'Scored At',
            align: 'left',
            formatter: (row: StudentExamAttemptQuestionDTO) => {
                if (row.questionType === 'Stimulus') {
                    return null;
                }
                if (!row.scoredDTUTC) return '-';
                return utcToLocal(row.scoredDTUTC).toLocaleString(undefined, formatOptions);
            },
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
        {
            dataKey: 'viewQuestion',
            header: 'View Question',
            align: 'center',
            formatter: (row: StudentExamAttemptQuestionDTO) => (
                <Button
                    size="small"
                    className="icon-button"
                    startIcon={<PreviewIcon style={{ width: '28px', height: '28px' }} />}
                    disabled={loadingQuestionId === row.id}
                    onClick={async () => {
                        if (previewQuestionData?.questionId === row.questionId) return;

                        setLoadingQuestionId(row.id);

                        // Open side panel immediately with loader
                        setPreviewQuestionData({
                            questionId: row.questionId,
                            submittedAnswer: row.submittedAnswer,
                            questionAnswer: null,
                        });

                        try {
                            const result = await apiRequest(
                                'get',
                                `/assessments/studentExamAttemptQuestions/viewWithAnswer/${row.id}`
                            );

                            setPreviewQuestionData({
                                questionId: row.questionId,
                                submittedAnswer: row.submittedAnswer,
                                questionAnswer: (result.data as { answer: string }).answer,
                            });
                        } catch (error) {
                            console.error('Failed to load question preview:', error);
                            setPreviewQuestionData({
                                questionId: row.questionId,
                                submittedAnswer: row.submittedAnswer,
                                questionAnswer: 'Error loading question',
                            });
                        } finally {
                            setLoadingQuestionId(null);
                        }
                    }}
                ></Button>
            ),
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
        },
    ];

    return (
        <Modal
            open={open}
            onClose={() => {
                setPreviewQuestionData(null);
                onClose();
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                    boxShadow: 24,
                    p: 0,
                    width: '85%',
                    height: '85%',
                    display: 'flex',
                    overflow: 'hidden',
                }}
            >
                {/* LEFT: Questions Table */}
                <Box
                    sx={{
                        width: previewQuestionData ? '60%' : '100%',
                        flexShrink: 0,
                        p: 3,
                        overflowY: 'auto',
                        transition: 'width 0.5s ease',
                    }}
                >
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                        Student: {student.studentName} <br />
                        Exam: {student.examName} (Attempt #{attempt.attemptNumber})
                    </Typography>

                    <Typography variant="body2" mb={3}>
                        Reporting Category:{' '}
                        <Label color={Constants.bandColors[attempt.reportingCategory]}>
                            {attempt.reportingCategory ?? '—'}
                        </Label>{' '}
                        | Score:{' '}
                        <strong> {attempt.score != null ? `${attempt.score}%` : '—'} </strong> |
                        Started:{' '}
                        <strong>
                            {attempt.startDTUTC
                                ? utcToLocal(attempt.startDTUTC).toLocaleString(
                                      undefined,
                                      formatOptions
                                  )
                                : '—'}
                        </strong>
                    </Typography>

                    {questions.length > 0 ? (
                        <DataTable
                            columns={columns}
                            data={questions}
                            tableName={`Attempt #${attempt.attemptNumber}`}
                            enableExportCSV
                            alternatingRowColor
                            defaultSortColumn="displayOrder"
                            defaultSortDirection="asc"
                        />
                    ) : (
                        <Typography color="text.secondary">
                            No questions available for this attempt.
                        </Typography>
                    )}

                    <Box textAlign="right" mt={3}>
                        <Button variant="outlined" onClick={onClose}>
                            Close
                        </Button>
                    </Box>
                </Box>

                {/* RIGHT: Question Preview Panel */}
                {previewQuestionData && (
                    <Box
                        sx={{
                            width: '40%',
                            flexShrink: 0,
                            borderLeft: '1px solid #ddd',
                            overflowY: 'auto',
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'opacity 0.5s ease',
                        }}
                    >
                        {previewQuestionData.questionAnswer === null ? (
                            <CircularProgress />
                        ) : previewQuestionData.questionAnswer === 'Error loading question' ? (
                            <Typography color="error">Error loading question</Typography>
                        ) : (
                            <PreviewQuestionModal
                                questionId={previewQuestionData.questionId}
                                submittedAnswer={previewQuestionData.submittedAnswer}
                                questionAnswer={previewQuestionData.questionAnswer}
                                onClose={() => setPreviewQuestionData(null)}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Modal>
    );
}
